import {
  BoundMultiSelectField,
  BoundSelectField,
  Button,
  Css,
  FormLines,
  Icon,
  IconButton,
  Palette,
  ScrollableParent,
  Step,
  useModal,
} from "@homebound/beam";
import { ObjectConfig, required, useFormState } from "@homebound/form-state";
import { useCallback } from "react";
import { StepActions, StepperProvider, useStepperContext } from "src/components/stepper";
import { NamedFragment } from "src/fragments/commonFragments.generated";
import { CostType, Maybe, SaveBidPackageInput } from "src/generated/graphqlTypes";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { formatList, pluralize, queryResult } from "src/utils";
import {
  BidPackageDetailPageDocument,
  PageBidPackageDetailFragment,
  useBidPackageDetailPageQuery,
} from "../BidPackageDetailPage.generated";
import { EditBidPackageLineItemModal } from "../add-bid-package/components/BidPackageLineItemModal";
import { BidProjectFields } from "../add-bid-package/components/BidProjectFields";
import {
  useEditBidPackageDetailsStepQuery,
  usePreviewSaveBidPackageLazyQuery,
  useSaveBidPackageMutation,
} from "./EditBidPackageModal.generated";

type EditBidPackageModalProps = { bidPackage: PageBidPackageDetailFragment };

const initialSteps: Step[] = [
  { label: "Details", state: "incomplete", value: "details" },
  { label: "Line Items", state: "incomplete", value: "line-items" },
];

export function EditBidPackageModal({ bidPackage }: EditBidPackageModalProps) {
  // Since we can change the cost codes and cost type, we need this to pull the freshest data from the cache
  const { data } = useBidPackageDetailPageQuery({ variables: { id: bidPackage.id }, fetchPolicy: "cache-only" });
  return (
    <StepperProvider steps={initialSteps} newLayout currentStep={initialSteps[1]}>
      <StepContent bidPackage={data?.bidPackage ?? bidPackage} />
    </StepperProvider>
  );
}

function StepContent({ bidPackage }: { bidPackage: PageBidPackageDetailFragment }) {
  const { currentStep } = useStepperContext();

  switch (currentStep.value) {
    case "details":
      return <DetailsStep bidPackage={bidPackage} />;
    case "line-items":
      return <EditBidPackageLineItemModal bidPackage={bidPackage} />;
    default:
      return null;
  }
}

function DetailsStep({ bidPackage }: { bidPackage: PageBidPackageDetailFragment }) {
  const query = useEditBidPackageDetailsStepQuery();
  return queryResult(query, (data) => <DetailsStepView bidPackage={bidPackage} {...data} />);
}

type DetailsStepViewProps = {
  bidPackage: PageBidPackageDetailFragment;
  markets: NamedFragment[];
  costCodes: NamedFragment[];
};

function DetailsStepView({ bidPackage, markets, costCodes }: DetailsStepViewProps) {
  const [saveBidPackage] = useSaveBidPackageMutation({ refetchQueries: [BidPackageDetailPageDocument] });
  const { confirmCostRemoval } = useConfirmCostRemoval();
  const { closeModal } = useModal();
  const { nextStep } = useStepperContext();
  const formState = useFormState({
    config: formConfig,
    init: {
      input: bidPackage,
      map: (bp) => ({
        id: bp.id,
        marketId: bp.projects?.first?.market?.id,
        projects: bp.projects?.map((p) => p.id),
        costCodes: bp.costCodes?.map((cc) => cc.id),
        costType: bp.costType?.code,
      }),
    },
  });
  const save = useCallback(
    async (exit: boolean) => {
      const { projects, marketId, id, costCodes, costType } = formState.value;
      const input = {
        id,
        // Both market-level and community-level bid packages use the projects field
        projects: marketId && (projects ?? []).isEmpty ? [marketId] : (projects ?? []),
        costCodes,
        costType,
      };
      // Removing a cost code drops its bid items, so confirm first if bidders already entered costs for them
      const removesCostCodes = formState.costCodes.originalValue?.some((cc) => !costCodes?.includes(cc));
      const save = () => saveBidPackage({ variables: { input } });
      const saved = removesCostCodes ? await confirmCostRemoval([input], save) : await save();
      if (!saved) return;
      if (exit) closeModal();
      else nextStep();
    },
    // TODO: validate this eslint-disable. It was automatically ignored when the oxlint migration restored `react-hooks/exhaustive-deps` to an error.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formState, saveBidPackage, confirmCostRemoval],
  );

  return (
    <ScrollableParent xss={Css.h("calc(100% - 144px)").$}>
      <header data-testid="bidLineItemsModal" css={Css.pb4.pt2.bb.bw1.bcGray200.bgWhite.$}>
        <div css={Css.df.fdc.$}>
          <div css={Css.ml("97%").$}>
            <IconButton icon="x" onClick={closeModal} />
          </div>
          <div css={Css.df.fdc.aic.jcc.$}>
            <h1 css={Css.xl2.$}>{bidPackage.name}</h1>
          </div>
        </div>
      </header>
      <div css={Css.df.gap3.mt2.aic.jcc.$}>
        <div css={Css.w50.p4.bgWhite.bshBasic.br12.mb3.$}>
          <div css={Css.lg.mb2.$}>Package Overview</div>
          <FormLines labelStyle="left" width="full">
            <BidProjectFields
              formState={formState}
              markets={markets}
              disabled={{ market: true, projects: !bidPackage.isUnitBased }}
            />
            <div css={Css.df.gap1.gray600.sm.$}>
              <Icon icon="infoCircle" color={Palette.Gray600} />
              If there are any newly relevant product offerings as a result of changing the bid, bid package updates
              will be created for them
            </div>
            <BoundMultiSelectField
              field={formState.costCodes}
              options={costCodes}
              multiline
              // Only unit-based packages can drop a cost code (and its bid items)
              disabledOptions={bidPackage.isUnitBased ? undefined : bidPackage.costCodes?.map((cc) => cc.id)}
            />
            <BoundSelectField
              field={formState.costType}
              getOptionValue={(ct) => ct.code}
              getOptionLabel={(ct) => ct.name}
              disabledOptions={
                // cannot change from material to labor or vice versa, or change subcontractor to labor or material
                bidPackage.costType?.code === CostType.Materials
                  ? [CostType.Labor]
                  : bidPackage.costType?.code === CostType.Labor
                    ? [CostType.Materials]
                    : [CostType.Labor, CostType.Materials]
              }
              options={[
                { code: CostType.Labor, name: "Labor" },
                { code: CostType.Materials, name: "Material" },
                { code: CostType.Subcontractor, name: "Subcontractor" },
              ]}
            />
          </FormLines>
        </div>
      </div>
      <StepActions>
        <>
          <Button variant="secondary" label="Save & Exit" size="lg" onClick={() => save(true)} />
          <Button label="Save" onClick={() => save(false)} size="lg" />
        </>
      </StepActions>
    </ScrollableParent>
  );
}

export type EditBidPackageFormState = Pick<SaveBidPackageInput, "projects" | "id" | "costCodes" | "costType"> & {
  marketId: Maybe<string>;
};

const formConfig: ObjectConfig<EditBidPackageFormState> = {
  id: { type: "value", rules: [required] },
  marketId: { type: "value", rules: [required] },
  projects: { type: "value" },
  costCodes: { type: "value", rules: [required] },
  costType: { type: "value", rules: [required] },
};

type ConfirmCostRemoval = <T>(inputs: SaveBidPackageInput[], save: () => Promise<T>) => Promise<T | undefined>;

/**
 * Dry runs each save with `previewSaveBidPackage` and, if any would remove bid lines or cost codes that already have
 * costs, or newly picks a primary cost code while other cost codes already have costs, asks the user to confirm.
 * Resolves `false` when the user cancels, so nothing should be persisted.
 */
export function useConfirmCostRemoval() {
  const { openModal } = useModal();
  // A dry run against the current server state, so never answer from a cached result
  const [previewSave, { loading: previewing }] = usePreviewSaveBidPackageLazyQuery({ fetchPolicy: "no-cache" });
  const confirmCostRemoval = useCallback<ConfirmCostRemoval>(
    async (inputs, save) => {
      const previews = await Promise.all(inputs.map((input) => previewSave({ variables: { input } })));
      const results = previews.map(({ data }) => data?.previewSaveBidPackage).compact();
      const removedCostedLines = results.flatMap((r) => r.removedCostedLines);
      const removedCostedCostCodes = results.flatMap((r) => r.removedCostedCostCodes);
      const costedNonPrimaryCostCodes = results.flatMap((r) => r.costedNonPrimaryCostCodes);
      if (removedCostedLines.isEmpty && removedCostedCostCodes.isEmpty && costedNonPrimaryCostCodes.isEmpty) {
        return save();
      }
      return new Promise((resolve) =>
        openModal({
          content: (
            <ConfirmCostRemovalModal
              removedCostedLines={removedCostedLines}
              removedCostedCostCodes={removedCostedCostCodes}
              costedNonPrimaryCostCodes={costedNonPrimaryCostCodes}
              onConfirm={() => save().then(resolve)}
            />
          ),
          onClose: () => resolve(undefined),
        }),
      );
    },
    [openModal, previewSave],
  );
  return { confirmCostRemoval, previewing };
}

type ConfirmCostRemovalModalProps = {
  removedCostedLines: { id: string }[];
  removedCostedCostCodes: { name: string }[];
  costedNonPrimaryCostCodes: { costCode: { name: string; displayName: string }; costCount: number }[];
  onConfirm: () => Promise<void>;
};

/**
 * Confirms a bid package save that `previewSaveBidPackage` says would drop bid lines or cost codes with costs, or
 * pick a primary cost code over other cost codes that already have costs (which marks them as included and deletes
 * those costs).
 */
function ConfirmCostRemovalModal({
  removedCostedLines,
  removedCostedCostCodes,
  costedNonPrimaryCostCodes,
  onConfirm,
}: ConfirmCostRemovalModalProps) {
  const removals = [
    removedCostedLines.nonEmpty
      ? `${removedCostedLines.length} bid lines that currently have costs on them`
      : undefined,
    removedCostedCostCodes.nonEmpty
      ? `the costs entered for ${removedCostedCostCodes.map((cc) => cc.name).join(", ")}`
      : undefined,
  ].compact();
  const coveredCosts = formatList(
    costedNonPrimaryCostCodes.map(
      ({ costCode, costCount }) => `${costCount} ${pluralize(costCount, "cost")} entered for ${costCode.displayName}`,
    ),
  );
  if (removals.isEmpty) {
    return (
      <ConfirmationModal
        danger
        icon={{ icon: "error", color: Palette.Orange600, inc: 4, xss: Css.fs0.$ }}
        title={`Delete Existing Costs for ${formatList(costedNonPrimaryCostCodes.map((c) => c.costCode.name))}?`}
        label="Continue"
        confirmationMessage={`This bid package has ${coveredCosts}. Are you sure you want to mark these costs as included and delete the existing costs?`}
        onConfirmAction={onConfirm}
      />
    );
  }
  return (
    <ConfirmationModal
      danger
      title={removedCostedLines.nonEmpty ? "Remove Bid Lines With Costs?" : "Remove Cost Codes With Costs?"}
      label="Yes"
      confirmationMessage={[
        `The changes you have made will remove ${removals.join(" and ")}.`,
        costedNonPrimaryCostCodes.nonEmpty
          ? `This bid package also has ${coveredCosts}, which will be marked as included and deleted.`
          : undefined,
        "Are you sure you want to make these changes?",
      ]
        .compact()
        .join(" ")}
      onConfirmAction={onConfirm}
    />
  );
}
